// Email Express — inline per-brand Klaviyo connection (Task #1776).
//
// A compact badge next to the page header shows whether this brand has its own
// Klaviyo Private API Key connected; clicking it opens a dialog to connect,
// test, edit, or remove the key. The key is encrypted server-side, never
// returned by the API, and the password input is never pre-filled. Brands
// without a connection keep sending via the global env-var fallback.

import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plug,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Trash2,
  ExternalLink,
} from "lucide-react";

type KlaviyoConnection = {
  id: string;
  brandId: string;
  status: string;
  lastCheckedAt: string | null;
  lastError: string | null;
  hasApiKey: boolean;
  createdAt: string;
  updatedAt: string;
};

const PERMISSIONS = [
  { label: "Campaigns", access: "Read/Write", required: true },
  { label: "Templates", access: "Read/Write", required: true },
  { label: "Flows", access: "Read/Write", required: true },
  { label: "Lists", access: "Read", required: false },
  { label: "Segments", access: "Read", required: false },
];

function StatusBadge({ status }: { status: string }) {
  if (status === "connected") {
    return (
      <Badge className="bg-green-600 hover:bg-green-700" data-testid="badge-klaviyo-status-connected">
        <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive" data-testid="badge-klaviyo-status-error">
        <XCircle className="h-3 w-3 mr-1" /> Error
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" data-testid="badge-klaviyo-status-pending">
      <AlertTriangle className="h-3 w-3 mr-1" /> Pending
    </Badge>
  );
}

export function KlaviyoConnectionBadge({ brandId }: { brandId: string }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  // Show the connect/edit form (vs the connected summary card).
  const [editing, setEditing] = useState(false);
  // Never pre-filled — the API never returns the key.
  const [apiKey, setApiKey] = useState("");
  // Inline result of the save-time validation (warning stays visible).
  const [inlineWarning, setInlineWarning] = useState<string | null>(null);

  const connection = useQuery<KlaviyoConnection | null>({
    queryKey: ["/api/brands", brandId, "klaviyo", "connection"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/klaviyo/connection`, {
        credentials: "include",
      });
      if (res.status === 204) return null;
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: !!brandId,
  });
  const conn = connection.data ?? null;

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/brands", brandId, "klaviyo", "connection"],
    });

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/klaviyo/connection`, {
        apiKey: apiKey.trim(),
      });
      return res.json() as Promise<{ ok: boolean; warning?: string; error?: string }>;
    },
    onSuccess: (data) => {
      setApiKey("");
      invalidate();
      if (data.ok) {
        setEditing(false);
        setInlineWarning(data.warning ?? null);
        toast({
          title: "Klaviyo connected",
          description: data.warning
            ? "Key works, with a note — see the connection details."
            : "The API key was validated successfully.",
        });
      } else {
        setEditing(false);
        setInlineWarning(null);
        toast({
          title: "Key saved, but validation failed",
          description: data.error ?? "Klaviyo rejected the key.",
          variant: "destructive",
        });
      }
    },
    onError: (e: Error) => {
      toast({ title: "Could not save", description: e.message, variant: "destructive" });
    },
  });

  const test = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/klaviyo/connection/test`);
      return res.json() as Promise<{ ok: boolean; warning?: string }>;
    },
    onSuccess: (data) => {
      invalidate();
      setInlineWarning(data.warning ?? null);
      toast({
        title: "Connection works",
        description: data.warning ?? "The API key was validated successfully.",
      });
    },
    onError: (e: Error) => {
      invalidate();
      toast({ title: "Test failed", description: e.message, variant: "destructive" });
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/brands/${brandId}/klaviyo/connection`);
    },
    onSuccess: () => {
      invalidate();
      setInlineWarning(null);
      setEditing(false);
      toast({
        title: "Connection removed",
        description: "Sends for this brand fall back to the workspace-wide Klaviyo key.",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Could not remove", description: e.message, variant: "destructive" });
    },
  });

  const isConnected = conn?.status === "connected";
  const isError = conn?.status === "error";

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setEditing(!conn);
        }}
        className={[
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors shrink-0",
          isConnected
            ? "border-green-600/40 bg-green-600/10 text-green-700 dark:text-green-400 hover:bg-green-600/20"
            : isError
              ? "border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20"
              : "border-border bg-muted/40 text-muted-foreground hover:text-foreground hover:bg-muted",
        ].join(" ")}
        data-testid="button-klaviyo-badge"
      >
        {isConnected ? (
          <CheckCircle2 className="h-3.5 w-3.5" />
        ) : isError ? (
          <XCircle className="h-3.5 w-3.5" />
        ) : (
          <Plug className="h-3.5 w-3.5" />
        )}
        {isConnected ? "Klaviyo: Connected" : isError ? "Klaviyo: Error" : "Connect Klaviyo"}
      </button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(false); setApiKey(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-klaviyo-connection">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5" /> Klaviyo connection
            </DialogTitle>
            <DialogDescription>
              Connect this brand's own Klaviyo Private API Key. The key is encrypted at rest and
              never shown again. Without a connection, sends use the workspace-wide key.
            </DialogDescription>
          </DialogHeader>

          {connection.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : conn && !editing ? (
            <div className="space-y-3" data-testid="view-klaviyo-summary">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="mt-0.5"><StatusBadge status={conn.status} /></dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Last checked</dt>
                  <dd className="mt-0.5" data-testid="text-klaviyo-last-checked">
                    {conn.lastCheckedAt ? new Date(conn.lastCheckedAt).toLocaleString() : "Never"}
                  </dd>
                </div>
              </dl>
              {conn.status === "error" && conn.lastError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Last error</AlertTitle>
                  <AlertDescription className="break-words" data-testid="text-klaviyo-last-error">
                    {conn.lastError}
                  </AlertDescription>
                </Alert>
              )}
              {(inlineWarning || (conn.status === "connected" && conn.lastError)) && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Heads up</AlertTitle>
                  <AlertDescription className="break-words" data-testid="text-klaviyo-warning">
                    {inlineWarning || conn.lastError}
                  </AlertDescription>
                </Alert>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => test.mutate()}
                  disabled={test.isPending}
                  data-testid="button-klaviyo-test"
                >
                  {test.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Testing...</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> Test connection</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setApiKey(""); setEditing(true); }}
                  data-testid="button-klaviyo-edit"
                >
                  Replace key
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                  data-testid="button-klaviyo-remove"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="form-klaviyo-connection">
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
                <p className="font-medium">Where to get the key</p>
                <p className="text-muted-foreground">
                  In Klaviyo: <span className="font-medium text-foreground">Settings → API Keys → Create Private API Key</span>.
                </p>
                <a
                  href="https://www.klaviyo.com/settings/account/api-keys"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                  data-testid="link-klaviyo-api-keys"
                >
                  Open Klaviyo API Keys <ExternalLink className="h-3 w-3" />
                </a>
                <p className="font-medium pt-1">Give it these permissions</p>
                <ul className="space-y-1">
                  {PERMISSIONS.map((p) => (
                    <li key={p.label} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
                      <span>
                        <span className="text-foreground">{p.label}</span> — {p.access}
                        {!p.required && <span className="italic"> (optional)</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Lists and Segments read are optional: without them, campaigns are still created
                  as drafts in Klaviyo — just without an auto-attached audience.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="klaviyo-api-key">Private API Key</Label>
                <Input
                  id="klaviyo-api-key"
                  type="password"
                  autoComplete="off"
                  placeholder="pk_..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  data-testid="input-klaviyo-api-key"
                />
                <p className="text-xs text-muted-foreground">
                  Encrypted before storage and never displayed back. Saving validates it right away.
                </p>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                {conn && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setApiKey(""); setEditing(false); }}
                    data-testid="button-klaviyo-cancel"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={save.isPending || !apiKey.trim()}
                  data-testid="button-klaviyo-save"
                >
                  {save.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Validating...</>
                  ) : (
                    "Save & validate"
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
